import { z } from 'zod/v4';

const normalizedEmail = (message = 'Некорректный формат email') =>
  z
    .string()
    .email(message)
    .transform((e) => e.toLowerCase().trim());

/**
 * Схема создания администратора.
 *
 * Поле `password` опционально:
 * - если SMTP настроен — можно не передавать, админ получит e-mail со ссылкой на установку пароля;
 * - если SMTP не настроен — `password` обязателен, иначе бэкенд вернёт 400.
 */
export const createAdminUserSchema = z.object({
  email: normalizedEmail(),
  password: z.string().min(6, 'Пароль должен быть не менее 6 символов').optional(),
  roleId: z.number().int().positive('Роль обязательна'),
  lang: z.string().min(2).max(5).optional(),
});

export type CreateAdminUserInput = z.infer<typeof createAdminUserSchema>;

/**
 * Схема обновления администратора
 */
export const updateAdminUserSchema = z.object({
  email: normalizedEmail().optional(),
  password: z.string().min(6, 'Пароль должен быть не менее 6 символов').optional(),
  roleId: z.number().int().positive().optional(),
});

export type UpdateAdminUserInput = z.infer<typeof updateAdminUserSchema>;

/**
 * Схема параметров запроса списка администраторов
 */
export const adminUsersQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(10),
  sortBy: z.enum(['createdAt', 'updatedAt', 'email', 'role']).default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
  search: z.string().optional(),
});

export type AdminUsersQueryInput = z.infer<typeof adminUsersQuerySchema>;

/**
 * Схема параметра ID администратора
 */
export const adminUserIdParamSchema = z.object({
  id: z.coerce.number().int().positive(),
});

export type AdminUserIdParam = z.infer<typeof adminUserIdParamSchema>;

/**
 * Response типы для администраторов
 */
export interface AdminUserResponse {
  id: number;
  username: string;
  email: string;
  roleId: number | null;
  roleDisplayName?: string;
  lastActivityAt: string | null;
  isOnline: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedAdminUsersResponse {
  data: AdminUserResponse[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Результат создания администратора.
 *
 * `mode === 'invite'` — создан аккаунт без пароля, на e-mail отправлена ссылка на установку пароля.
 * `mode === 'password'` — пароль задан напрямую (классический режим без SMTP).
 */
export type CreateAdminUserResult =
  | { mode: 'invite'; email: string }
  | { mode: 'password'; admin: AdminUserResponse };
